import React from "react";
import { Bar, Pie } from "react-chartjs-2";
import ScrollableTable from "./ScrollableTable";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement
} from "chart.js";

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

const stats = {
  overview: [
    { label: "Total Genes", value: 213885 },
    { label: "Unique Genes", value: 10038 },
    { label: "Plasmids", value: 11942 },
    { label: "Organisms", value: 2005 },

  ],
  category_distribution: {
    "Conjugation": 28.2,
    "Plasmid Maintenance, Backbone and Regulation": 13.0,
    "Metabolism": 20.7,
    "Stress Response": 6.7,
    "Virulence and Defense Mechanism": 4.3,
    "Metal and Biocide Resistance": 7.5,
    "Antibiotic Resistance": 4.3,
    "Non-Conjugative DNA Mobility": 6.4,
    "Toxin Antitoxin System": 3.5,
    "Other": 5.4
  },
  functional_annotation: {
    "Product Descriptions": 91.5,
    "COG Classifications": 47.4,
    "Gene Ontology (MF)": 62.5,
    "KEGG Pathways": 19.9
  },
  isolation_source: {
    "Plasmids with data": 17031,
    categories: {
      Other: 5922,
      Environmental: 2684,
      Clinical: 3287,
      Laboratory: 49 
    }
  },
  incompatibility_groups: {
    typed: 8353,
    unique_types: 326,
    top_types: {
      ColRNAI: 896,
      "IncFIB(AP001918)": 468,
      "IncFIB(K)": 352,
      IncI1: 334,
      IncN: 280,
      Col440I: 870,
      Col440II: 456,
      IncFII_K: 390,
      IncX1: 312,
      IncC: 278
    }
  }
};

function DatabaseStats() {
  return (
    <div className="space-y-12 max-w-7xl mx-auto p-4">
      {/* Core Stats */}
      <section>
        <h2 className="text-3xl font-bold mb-6">📊 PlasAnn Database Overview</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {stats.overview.map((item) => (
            <div key={item.label} className="bg-white shadow rounded p-4 text-center">
              <div className="text-gray-500 text-sm">{item.label}</div>
              <div className="text-2xl font-bold text-blue-800">{item.value.toLocaleString()}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Category Distribution + Isolation */}
      <section>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Category Distribution */}
          <div className="bg-white p-4 shadow rounded">
            <h3 className="text-xl font-semibold mb-2">🧬 Gene Category Distribution</h3>
            <Pie
              data={{
                labels: Object.keys(stats.category_distribution),
                datasets: [
                  {
                    data: Object.values(stats.category_distribution),
                    backgroundColor: [
                      "#2563eb", // Conjugation
                      "#10b981", // Plasmid Maintenance
                      "#f59e0b", // Metabolism
                      "#ef4444", // Stress Response
                      "#8b5cf6", // Virulence and Defense
                      "#06b6d4", // Metal and Biocide Resistance
                      "#f97316", // Antibiotic Resistance
                      "#84cc16", // Non-Conjugative DNA Mobility
                      "#ec4899", // Toxin Antitoxin System
                      "#6b7280"  // Other
                    ]
                  }
                ]
              }}
              options={{ 
                plugins: { 
                  legend: { 
                    position: "bottom",
                    labels: {
                      maxWidth: 200,
                      usePointStyle: true
                    }
                  },
                  tooltip: {
                    callbacks: {
                      label: function(context) {
                        return context.label + ': ' + context.parsed + '%';
                      }
                    }
                  }
                } 
              }}
            />
          </div>

          {/* Isolation */}
          <div className="bg-white p-4 shadow rounded">
            <h3 className="text-xl font-semibold mb-2">🌍 Isolation Sources</h3>
            <Pie
              data={{
                labels: Object.keys(stats.isolation_source.categories),
                datasets: [
                  {
                    data: Object.values(stats.isolation_source.categories),
                    backgroundColor: ["#f97316", "#22c55e", "#0ea5e9", "#6366f1"]
                  }
                ]
              }}
              options={{ plugins: { legend: { position: "bottom" } } }}
            />
          </div>
        </div>
      </section>

      {/* Functional + INC Types */}
      <section>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Functional */}
          <div className="bg-white p-4 shadow rounded">
            <h3 className="text-xl font-semibold mb-2">🧬 Functional Annotation Coverage</h3>
            <Bar
              data={{
                labels: Object.keys(stats.functional_annotation),
                datasets: [
                  {
                    label: "% Coverage",
                    data: Object.values(stats.functional_annotation),
                    backgroundColor: "#3b82f6"
                  }
                ]
              }}
              options={{
                scales: {
                  y: {
                    beginAtZero: true,
                    max: 100,
                    title: { display: true, text: "%" }
                  }
                }
              }}
            />
          </div>

          {/* INC Types */}
          <div className="bg-white p-4 shadow rounded">
            <h3 className="text-xl font-semibold mb-2">🧫 Top 10 Incompatibility Groups</h3>
            <Bar
              data={{
                labels: Object.keys(stats.incompatibility_groups.top_types),
                datasets: [
                  {
                    label: "Plasmid Count",
                    data: Object.values(stats.incompatibility_groups.top_types),
                    backgroundColor: "#14b8a6"
                  }
                ]
              }}
              options={{
                indexAxis: "y",
                responsive: true,
                scales: {
                  x: {
                    beginAtZero: true,
                    title: { display: true, text: "Count" }
                  }
                }
              }}
            />
          </div>
        </div>
      </section>
      {/* 👇 Add this new scrollable table section here */}
      <ScrollableTable />
    </div>
  );
}

export default DatabaseStats;